import createSelector from 'selectorator'
import { path, identity } from 'ramda'
import { NAMESPACE } from './constants'
import { RootState } from 'src/state/types'
import { TAccountingInitialState } from './state'
import { JsonResponse } from './types/JsonResponse'
import { Base64PdfResponse } from './types/Base64PdfResponse'
import { BillingAgencySettingsResponse } from './types/BillingAgencySettingsResponse'

type TAccountingSliceKeys = keyof TAccountingInitialState
type TAccountingSlice = TAccountingInitialState[keyof TAccountingInitialState]
// const getSlice = (key: TAccountingSliceKeys) =>
//   createSelector<RootState, TAccountingSlice>([`${NAMESPACE}.${key}`], identity)

const getSlice = (key: TAccountingSliceKeys) =>
  createSelector<RootState, TAccountingSlice>([`${NAMESPACE}.${key}`], identity)

export const getBankStatementJson = getSlice('bankStatementJson')
export const getBankStatementPdf = getSlice('bankStatementPdf')
export const getBankStatementCsv = getSlice('bankStatementCsv')

// not used at the moment
// export const getCustomerStatements = getSlice('customerStatements')

export const getCustomerStatementJson = getSlice('customerStatementJson')
export const getCustomerStatementPdf = getSlice('customerStatementPdf')
export const getCustomerStatementCsv = getSlice('customerStatementCsv')
export const getConsolidatedStatementJson = getSlice('consolidatedStatementJson')
export const getConsolidatedStatementPdf = getSlice('consolidatedStatementPdf')
export const getConsolidatedStatementCsv = getSlice('consolidatedStatementCsv')
export const getPortfolioStatements = getSlice('portfolioStatementJson')
export const getPortfolioStatementPdf = getSlice('portfolioStatementPdf')
export const getPortfolioStatementCsv = getSlice('portfolioStatementCsv')

/** @todo rename these to `customer&*` when introducing general deposit statement */
export const getDepositStatementJson = getSlice('depositStatementJson')
export const getDepositStatementPdf = getSlice('depositStatementPdf')
export const getDepositStatementCsv = getSlice('depositStatementCsv')

export const getDepositBankStatementJson = getSlice('depositBankStatementJson')
export const getDepositBankStatementPdf = getSlice('depositBankStatementPdf')
export const getDepositBankStatementCsv = getSlice('depositBankStatementCsv')

export const getCommissionStatementJson = getSlice('commissionStatementJson')
export const getCommissionStatementPdf = getSlice('commissionStatementPdf')
export const getCommissionStatementCsv = getSlice('commissionStatementCsv')

// not used at the moment
// export const getStatementById = createSelector(
//   [getCustomerStatements],
//   statements => (id: any) => statements.find((stmt: any) => stmt.id === id),
// )

export const getCustomerStatementJsonById = createSelector<
  RootState,
  (customerId: string, accountId: string) => JsonResponse | undefined
>([getCustomerStatementJson], s => (customerId, accountId) => path([`${customerId}${accountId}`], s))

export const getCustomerStatementPdfById = createSelector<
  RootState,
  (customerId: string, accountId: string) => Base64PdfResponse | undefined
>([getCustomerStatementPdf], s => (customerId, accountId) => path([`${customerId}${accountId}`], s))

export const getCustomerStatementCsvById = createSelector<
  RootState,
  (customerId: string, accountId: string) => string | undefined
>([getCustomerStatementCsv], s => (customerId, accountId) => path([`${customerId}${accountId}`], s))

export const getConsolidatedStatementJsonById = createSelector<
  RootState,
  (partyId: string) => JsonResponse | undefined
>([getConsolidatedStatementJson], s => partyId => path([partyId], s))

export const getConsolidatedStatementPdfById = createSelector<
  RootState,
  (partyId: string) => Base64PdfResponse | undefined
>([getConsolidatedStatementPdf], s => partyId => path([partyId], s))

export const getConsolidatedStatementCsvById = createSelector<RootState, (partyId: string) => string | undefined>(
  [getConsolidatedStatementCsv],
  s => partyId => path([partyId], s),
)

export const getPortfolioStatementById = createSelector<RootState, (id: string) => JsonResponse | undefined>(
  [getPortfolioStatements],
  s => id => path([id], s),
)

export const getPortfolioStatementPdfById = createSelector<RootState, (id: string) => Base64PdfResponse | undefined>(
  [getPortfolioStatementPdf],
  s => id => path([id], s),
)

export const getPortfolioStatementCsvById = createSelector<RootState, (id: string) => string | undefined>(
  [getPortfolioStatementCsv],
  s => id => path([id], s),
)

export const getDepositStatementJsonById = createSelector<RootState, (id: string) => JsonResponse | undefined>(
  [getDepositStatementJson],
  s => id => path([id], s),
)

export const getDepositStatementPdfById = createSelector<RootState, (id: string) => Base64PdfResponse | undefined>(
  [getDepositStatementPdf],
  s => id => path([id], s),
)

export const getDepositStatementCsvById = createSelector<RootState, (id: string) => string | undefined>(
  [getDepositStatementCsv],
  s => id => path([id], s),
)

export const getCommissionStatementJsonById = createSelector<RootState, (id: string) => JsonResponse | undefined>(
  [getCommissionStatementJson],
  s => id => path([id], s),
)

export const getStats = getSlice('stats')

// Not getSlice — that widens to the union of every slice in the state.
export const getBillingAgencySettings = createSelector<RootState, BillingAgencySettingsResponse | null>(
  [`${NAMESPACE}.billingAgencySettings`],
  identity,
)

// Null until loaded, so the UI holds off instead of rendering a number
// derived from an empty slice.
export const getNextBillingInvoiceNumber = createSelector<RootState, number | null>(
  [getBillingAgencySettings],
  (settings: BillingAgencySettingsResponse | null) => (settings ? settings.lastBillingInvoiceNumber + 1 : null),
)
